/**
 * Links. Kit components render plain anchors, so a link is an address: `href("/people?view=held")`.
 * v2 is served at the root (#704), so the address is the route itself; links still build theirs
 * here, so the app's base lives in one place. The shell turns a plain click on one into a router
 * navigation (`routeOf`); a modified click, or one on a `target` or `download` link, is left to the
 * browser, as is any address the server owns (`/api/`, `/admin/`, `/static/`).
 */
import type { MouseEvent } from "react";

export const href = (to: string): string => to;

const SERVER = /^\/(?:api|admin|django-admin|static)(?:\/|$)/;

/** The address of the in-app link `target` is in (or is), or null for none, or one the browser handles. */
export function inAppUrl(target: EventTarget | null): URL | null {
  const anchor = target instanceof Element ? target.closest<HTMLAnchorElement>("a[href]") : null;
  if (!anchor || anchor.target || anchor.hasAttribute("download")) return null;
  const url = new URL(anchor.href, window.location.href);
  return url.origin !== window.location.origin || SERVER.test(url.pathname) ? null : url;
}

/** The route a click on an in-app anchor asks for, or null when the browser should handle the click. */
export function routeOf(event: MouseEvent): string | null {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
  const url = inAppUrl(event.target);
  return url && `${url.pathname}${url.search}${url.hash}`;
}
